import { omit } from "lodash-es";

import {
  JsonApiInludedRelationship,
  JsonApiRelationship,
  JsonApiResourceDeserialized,
} from "src/api/jsonApiTypes";
import {
  CaseResponse,
  CaseTemplateAttributes,
  CaseTemplateVersionAttributes,
  EntityDetailsCaseBlockConfigAttributes,
  EventViewerCaseBlockConfigAttributes,
  RelatedEntitiesCaseBlockConfigAttributes,
  RelatedEventsCaseBlockConfigAttributes,
  RelatedCasesCaseBlockConfigAttributes,
  CasesResponse,
  FileCategoriesAttributes,
  CaseDetailsCaseBlockConfigAttributes,
  RunningActionAttributes,
  CaseActionAttributes,
  CaseTaskAttributes,
  JsonApiResourceObjectBaseModelCaseActionFormMeta,
  CaseTaskFormMeta,
  CaseHistoryEventsResponse,
  EventData,
  CaseTemplateTaskOutput,
} from "src/clients/case-management";

// Created custom type because the backend generated type was incorrect
type CasePropertiesResource = {
  id: string;
  type: "case_properties";
  attributes: {
    status: string;
    sub_status?: string;
    [key: string]: unknown;
  };
};

type TemplateResource = {
  id: string;
  type: "case_templates";
  attributes: CaseTemplateAttributes;
};

type TemplateVersionResource = {
  id: string;
  type: "case_template_versions";
  attributes: CaseTemplateVersionAttributes;
};

type FileCategoryResource = {
  id: string;
  type: "file_categories";
  attributes: FileCategoriesAttributes;
};

type RunningActionResource = {
  id: string;
  type: "running_actions";
  attributes: RunningActionAttributes;
};

type CaseBlockResource<A> = {
  id: string;
  type: "case_blocks";
  attributes: A;
};

// MARKDOWN BLOCK
export type CaseBlockMarkdownData = {
  type: "case_block_data";
  id: string;
  content: string | null;
  meta: null;
  relationships: null;
};

export type CaseBlockMarkdown = CaseBlockResource<{
  kind: "markdown";
  data: CaseBlockMarkdownData;
}>;

// ENTITY DETAILS BLOCK
type CaseBlockEntityDetailsConfig = {
  type: "case_block_configs";
} & EntityDetailsCaseBlockConfigAttributes;

type CaseBlockEntityDetailsExternalData = {
  type: "case_block_data";
  id: string;
};

export type CaseBlockEntityDetails = CaseBlockResource<{
  kind: "entity_details";
  config: CaseBlockEntityDetailsConfig;
  external_data: CaseBlockEntityDetailsExternalData;
}>;

// CASE DETAILS BLOCK
type CaseBlockCaseDetailsConfig = {
  type: "case_block_configs";
} & CaseDetailsCaseBlockConfigAttributes;

type CaseBlockCaseDetailsExternalData = {
  type: "case_block_data";
  id: string;
};

export type CaseBlockCaseDetails = CaseBlockResource<{
  kind: "case_details";
  config: CaseBlockCaseDetailsConfig;
  external_data: CaseBlockCaseDetailsExternalData;
}>;

// TABLE BLOCK
type CaseBlockTableData = {
  type: "case_block_data";
  id: string;
  record: Record<string, unknown>;
};

export type CaseBlockTable = CaseBlockResource<{
  kind: "table";
  data: CaseBlockTableData;
}>;

// EVENT DETAILS BLOCK
type CaseBlockEventViewConfig = {
  type: "case_block_configs";
} & EventViewerCaseBlockConfigAttributes;

type CaseBlockEntityViewExternalData = {
  type: "case_block_data";
  id: string;
};

export type CaseBlockEventView = CaseBlockResource<{
  kind: "event_details";
  config: CaseBlockEventViewConfig;
  external_data: CaseBlockEntityViewExternalData;
}>;

type CaseBlockEventsListConfig = {
  type: "case_block_configs";
} & RelatedEventsCaseBlockConfigAttributes;

type CaseBlockEventsListExternalData = {
  type: "case_block_data";
  id: string;
};

export type CaseBlockEventsList = CaseBlockResource<{
  kind: "related_events";
  config: CaseBlockEventsListConfig;
  external_data: CaseBlockEventsListExternalData;
}>;

type CaseBlockRelatedEntitiesConfig = {
  type: "case_block_configs";
} & RelatedEntitiesCaseBlockConfigAttributes;

type CaseBlockRelatedEntitiesExternalData = {
  type: "case_block_data";
  id: string;
};

export type CaseBlockRelatedEntities = CaseBlockResource<{
  kind: "related_entities";
  config: CaseBlockRelatedEntitiesConfig;
  external_data: CaseBlockRelatedEntitiesExternalData;
}>;

type CaseBlockRelatedCasesConfig = {
  type: "case_block_configs";
} & RelatedCasesCaseBlockConfigAttributes;

type CaseBlockRelatedCasesExternalData = {
  type: "case_block_data";
  id: string;
};

export type CaseBlockRelatedCases = CaseBlockResource<{
  kind: "related_cases";
  config: CaseBlockRelatedCasesConfig;
  external_data: CaseBlockRelatedCasesExternalData;
}>;

type CaseBlocks =
  | CaseBlockMarkdown
  | CaseBlockEntityDetails
  | CaseBlockCaseDetails
  | CaseBlockTable
  | CaseBlockEventView
  | CaseBlockEventsList
  | CaseBlockRelatedEntities
  | CaseBlockRelatedCases;

type CaseAdditionalInputResource = {
  id: string;
  type: "additional_inputs";
  attributes: {
    [key: string]: unknown;
  };
};

type CaseActionResource = {
  id: string;
  type: "case_actions";
  attributes: CaseActionAttributes & {
    form: {
      type: "case_action_forms";
    } & JsonApiResourceObjectBaseModelCaseActionFormMeta;
  };
};

type CaseTaskResource = {
  id: string;
  type: "case_tasks";
  attributes: CaseTaskAttributes & {
    form?: {
      id?: string;
      type: "case_task_forms";
      meta?: CaseTaskFormMeta | null;
      [key: string]: unknown;
    } | null;
  };
};

export type CaseDeserialized = JsonApiResourceDeserialized<
  CaseResponse,
  {
    additional_inputs: JsonApiInludedRelationship<
      CaseAdditionalInputResource,
      "additional_inputs"
    >;
    assignee: JsonApiRelationship<"assignees">;
    case_properties: JsonApiInludedRelationship<
      CasePropertiesResource,
      "case_properties"
    >;
    queue: JsonApiRelationship<"queues">;
    template: JsonApiInludedRelationship<TemplateResource, "case_templates">;
    template_version: JsonApiInludedRelationship<
      TemplateVersionResource,
      "case_template_versions"
    >;
    case_blocks: JsonApiInludedRelationship<CaseBlocks, "case_blocks">[];
    file_categories: JsonApiInludedRelationship<
      FileCategoryResource,
      "file_categories"
    >[];
    running_actions: JsonApiInludedRelationship<
      RunningActionResource,
      "running_actions"
    >[];
    case_actions: JsonApiInludedRelationship<
      CaseActionResource,
      "case_actions"
    >[];
    case_tasks: JsonApiInludedRelationship<CaseTaskResource, "case_tasks">[];
  }
>;

export type CasesDeserialized = JsonApiResourceDeserialized<
  CasesResponse,
  {
    case_properties: JsonApiInludedRelationship<
      CasePropertiesResource,
      "case_properties"
    >;
    assignee: JsonApiRelationship<"assignees">;
    queue: JsonApiRelationship<"queues">;
    template: Exclude<JsonApiRelationship<"case_templates">, null>;
    template_version: Exclude<
      JsonApiRelationship<"case_template_versions">,
      null
    >;
  }
>;

export type CaseHistoryDeserialized = JsonApiResourceDeserialized<
  CaseHistoryEventsResponse,
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  {}
>;

export type CaseHistoryEvent<T extends string> = Omit<
  CaseHistoryDeserialized["data"][number],
  "event_type" | "event_data"
> & {
  event_type: T;
  event_data: Extract<EventData, { event_type: T }>;
};

type EventDataTypes = EventData["event_type"];

export type GroupableRootEventType =
  | "case_creation_flow_initiated"
  | "action_triggered";

export type GroupableEventType =
  | GroupableRootEventType
  | "action_completed"
  | "action_failed";

export type HistoryEntryActionGroup = {
  id: string; // running_action_id
  type: "group";
  event_type: "case_creation_flow_initiated" | "action_triggered";
  events: CaseHistoryEvent<GroupableEventType>[];
};

// Pinned events that can be grouped by time and block
export type PinnedGroupableEventType =
  | "pinned_entities_updated"
  | "pinned_events_updated";

export type HistoryEntryPinnedEntitiesGroup = {
  id: string;
  type: "group";
  event_type: "pinned_entities_updated";
  events: CaseHistoryEvent<"pinned_entities_updated">[];
};

export type HistoryEntryPinnedEventsGroup = {
  id: string;
  type: "group";
  event_type: "pinned_events_updated";
  events: CaseHistoryEvent<"pinned_events_updated">[];
};

export type SingleEventType = Exclude<
  EventDataTypes,
  GroupableEventType | PinnedGroupableEventType
>;

export type HistoryEntrySingle = {
  id: string; // event.id
  type: "single";
  event_type: SingleEventType;
  event: CaseHistoryEvent<SingleEventType>;
};

export type HistoryEntry =
  | HistoryEntryActionGroup
  | HistoryEntryPinnedEntitiesGroup
  | HistoryEntryPinnedEventsGroup
  | HistoryEntrySingle;
// ID extraction utilities for format: caseid#status#task_key
export const TaskIdExtractor = {
  /**
   * Extract case ID from full task ID: caseid#status#task_key -> caseid
   */
  getCaseId: (fullTaskId: string): string => {
    return fullTaskId.split("#")[0] || "";
  },

  /**
   * Extract task key from full task ID: caseid#status#task_key -> task_key
   */
  getTaskKey: (fullTaskId: string): string => {
    return fullTaskId.split("#")[2] || "";
  },

  /**
   * Extract status and task key for URL: caseid#status#task_key -> status#task_key
   */
  getStatusAndTaskKey: (fullTaskId: string): string => {
    const parts = fullTaskId.split("#");
    return parts.slice(1).join("#");
  },

  /**
   * Get the full task ID (identity function for clarity)
   */
  getFullId: (fullTaskId: string): string => {
    return fullTaskId;
  },
};

// Task transformation utilities
export type AvailableCaseTask = CaseDeserialized["data"]["case_tasks"][number];

export type CaseTask = {
  id: string;
  key: string;
  title: string;
  isCompleted: boolean;
  description: CaseTemplateTaskOutput["description"];
  templateForm: CaseTemplateTaskOutput["form"];
  formValues: Record<string, unknown>;
  formMeta?: {
    id: string;
    etag?: number;
  };
};

export const transformCaseTask = (
  caseTask: AvailableCaseTask,
  getTemplateTask: (taskKey: string) => CaseTemplateTaskOutput | undefined,
): CaseTask => {
  const taskKey = TaskIdExtractor.getTaskKey(caseTask.id);
  const templateTask = getTemplateTask(taskKey);

  const caseTaskForm = caseTask.form as
    | (Record<string, unknown> & {
        id?: string;
        meta?: { etag?: number | null } | null;
      })
    | undefined;

  const formValues = caseTaskForm
    ? (omit(caseTaskForm, ["type", "meta", "relationships", "id"]) as Record<
        string,
        unknown
      >)
    : {};

  return {
    id: caseTask.id,
    key: taskKey,
    title: templateTask?.display_name || taskKey,
    description: templateTask?.description ?? null,
    templateForm: templateTask?.form ?? null,
    formValues,
    formMeta: caseTaskForm?.id
      ? {
          id: TaskIdExtractor.getStatusAndTaskKey(String(caseTaskForm.id)),
          etag: caseTaskForm.meta?.etag ?? undefined,
        }
      : undefined,
    isCompleted: caseTask.is_completed,
  };
};
